import { computed, onBeforeUnmount, ref, type Ref } from "vue";
import { useRouter } from "vue-router";
import { onClickOutside, useEventListener, useMediaQuery } from "@vueuse/core";

/*****************************************************************************
 * Open/close behaviour shared by the header <details> dropdowns
 * (Notifications, Support, Account, Social, and the public Products menu).
 *
 * They used to open on mouseenter and toggle on click, and closed only on
 * mouseleave or a click inside. On touch a tap fired both, so a menu could
 * open and immediately close, or stay open over the page across navigation;
 * several could be open at once and Escape did nothing (nav-12, mobile-8).
 *
 * - Exclusive: every menu carries name="header-menu", so opening one closes
 *   the others natively; `toggle` repeats that by hand for browsers that
 *   predate the attribute.
 * - Closes on an outside click/tap, on Escape, when focus leaves it, and
 *   after every route change.
 * - Hover opens it only where the primary pointer can hover. A touch tap
 *   emulates mouseenter, and that emulated hover was what fought the
 *   summary's own toggle.
 *****************************************************************************/

/** The `name` shared by the header menus, which makes them an exclusive group. */
export const HEADER_MENU_GROUP = "header-menu";

/** A real hover-capable pointer (a mouse or trackpad), not a touchscreen's emulated hover. */
export const HOVER_POINTER_QUERY = "(hover: hover) and (pointer: fine)";

/**
 * The same pointer at lg and up. For a menu whose panel leaves its summary's box below lg (the
 * viewport-pinned notifications panel): the mouse cannot cross the gap without a mouseleave, so
 * there it has to open on click and close on an outside click like a touchscreen.
 */
export const LG_HOVER_POINTER_QUERY = `${ HOVER_POINTER_QUERY } and (min-width: 1024px)`;

export type HeaderMenuOpenEvent = {
    /** True when a hovering pointer opened it, which may only be the mouse passing across. */
    byHover: boolean,
};

export type HeaderMenuOptions = {
    /** Runs each time the menu opens, however it was opened. */
    onOpen?: ( event: HeaderMenuOpenEvent ) => void,
    /** Runs each time the menu closes, however it was closed. */
    onClose?: () => void,
    /** Where hover opens and closes the menu. Defaults to HOVER_POINTER_QUERY. */
    hoverQuery?: string,
};

export function useHeaderMenu( menu: Readonly< Ref< HTMLDetailsElement | null > >, options: HeaderMenuOptions = {} ) {
    const isOpen = ref< boolean >( false );
    const canHover = useMediaQuery( options.hoverQuery ?? HOVER_POINTER_QUERY );

    // Set when the pointer opened the menu, so the click that usually follows on the summary
    // (the member points at "Account", then clicks it) does not immediately toggle it shut.
    let openedByHover = false;

    function open(): void {
        if ( menu.value ) {
            menu.value.open = true;
        }
    }

    function close(): void {
        // Reset here too: a quick enter/leave can coalesce into no toggle at all, which would leave
        // the flag set for whatever opens the menu next.
        openedByHover = false;
        if ( menu.value?.open ) {
            menu.value.open = false;
        }
    }

    function closeOtherMenus(): void {
        document.querySelectorAll< HTMLDetailsElement >( `details[name="${ HEADER_MENU_GROUP }"][open]` ).forEach( ( other ) => {
            if ( other !== menu.value ) {
                other.open = false;
            }
        } );
    }

    // `toggle` fires for every way the state changes (summary tap, keyboard, hover, close()), so
    // it is the one place isOpen and the callbacks are driven from.
    function onToggle(): void {
        const nowOpen = !!menu.value?.open;
        if ( nowOpen === isOpen.value ) {
            return;
        }

        isOpen.value = nowOpen;
        if ( nowOpen ) {
            closeOtherMenus();
            options.onOpen?.( { byHover: openedByHover } );
        } else {
            openedByHover = false;
            options.onClose?.();
        }
    }

    function onSummaryClick( event: MouseEvent ): void {
        if ( openedByHover && menu.value?.open ) {
            event.preventDefault();
        }
        openedByHover = false;
    }

    function onMouseEnter(): void {
        if ( !menu.value?.open ) {
            openedByHover = true;
            open();
        }
    }

    // Bound with v-on so that on touch devices no mouse handlers exist at all.
    const noHandlers: Record< string, () => void > = {};
    const pointerHandlers: Record< string, () => void > = { mouseenter: onMouseEnter, mouseleave: close };
    const hoverHandlers = computed< Record< string, () => void > >( () => canHover.value ? pointerHandlers : noHandlers );

    function onFocusOut( event: FocusEvent ): void {
        // Only a move to a known element outside closes it. relatedTarget is null when a click
        // lands on something unfocusable inside the panel, or in Safari on any button.
        const next = event.relatedTarget as Node | null;
        if ( next && menu.value && !menu.value.contains( next ) ) {
            close();
        }
    }

    onClickOutside( menu, close );

    useEventListener( document, "keydown", ( event: KeyboardEvent ) => {
        if ( event.key !== "Escape" || !menu.value?.open ) {
            return;
        }

        const hadFocus = menu.value.contains( document.activeElement );
        close();
        if ( hadFocus ) {
            menu.value.querySelector< HTMLElement >( "summary" )?.focus();
        }
    } );

    // afterEach returns its own unregister function. The header outlives most routes but not all
    // (the layout swaps on sign-in/out), so keep it or every remount stacks another global hook.
    const router = useRouter();
    const unregisterAfterEach = router?.afterEach( () => close() );
    onBeforeUnmount( () => unregisterAfterEach?.() );

    return {
        close,
        hoverHandlers,
        isOpen,
        menuGroup: HEADER_MENU_GROUP,
        onFocusOut,
        onSummaryClick,
        onToggle,
    };
}
